import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f92832a4-f773-4854-853a-afbb4cec93dc/artifacts/hfvgmn1m_JS%20Blond%20Logo%20white.png";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const leftLinks = [
    { label: "Services", path: "/services" },
    { label: "Co-Working", path: "/coworking" },
    { label: "Shop", path: "https://js-blondsalon.myshopify.com", external: true },
  ];

  const rightLinks = [
    { label: "Education", path: "/education" },
    { label: "Kontakt", path: "/kontakt" },
  ];

  const allLinks = [...leftLinks, ...rightLinks];

  const isActive = (path) => location.pathname === path;

  const getLinkClass = (path) => {
    const baseClass = "nav-link transition-colors text-sm uppercase tracking-widest";
    if (isActive(path)) {
      return `${baseClass} text-[#D4AF37]`;
    }
    if (isScrolled || !isHomePage) {
      return `${baseClass} text-[#2C2C2C] hover:text-[#9A8C73]`;
    }
    return `${baseClass} text-white hover:text-white/70`;
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled || !isHomePage
            ? "glass shadow-card py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container-padding">
          {/* Desktop Navigation - Logo centered */}
          <div className="hidden md:flex items-center justify-between">
            {/* Left Links */}
            <nav className="flex items-center gap-8 flex-1">
              {leftLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={getLinkClass(link.path)}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.path.replace('/', '')}`}
                    className={getLinkClass(link.path)}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </nav>

            {/* Center Logo */}
            <Link to="/" className="flex-shrink-0 mx-8">
              <motion.div
                data-testid="logo-link"
                className="flex items-center justify-center cursor-pointer"
                whileHover={{ scale: 1.02 }}
              >
                <img 
                  src={LOGO_URL} 
                  alt="JS Blond Salon" 
                  className="h-12 md:h-14 w-auto transition-all duration-300"
                  style={{ 
                    filter: (isScrolled || !isHomePage) ? "invert(1)" : "invert(0)" 
                  }}
                />
              </motion.div>
            </Link>

            {/* Right Links + CTA */}
            <div className="flex items-center gap-8 flex-1 justify-end">
              {rightLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.replace('/', '')}`}
                  className={getLinkClass(link.path)}
                >
                  {link.label}
                </Link>
              ))}
              <motion.a
                href="https://www.phorest.com/salon/jsblondsalonughaftungsbeschrae"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-book-btn"
                className={`px-6 py-3 rounded-full uppercase tracking-widest text-xs font-bold transition-all duration-300 ${
                  (isScrolled || !isHomePage)
                    ? "bg-[#2C2C2C] text-white hover:bg-[#9A8C73]"
                    : "bg-white text-[#2C2C2C] hover:bg-[#D4AF37] hover:text-white"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Buchen
              </motion.a>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center justify-between">
            {/* Mobile Menu Button */}
            <button
              className={`p-2 ${(isScrolled || !isHomePage) ? "text-[#2C2C2C]" : "text-white"}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={28} /> : <List size={28} />}
            </button>

            {/* Mobile Logo - Centered */}
            <Link to="/">
              <img 
                src={LOGO_URL} 
                alt="JS Blond Salon" 
                className="h-10 w-auto"
                style={{ 
                  filter: (isScrolled || !isHomePage) ? "invert(1)" : "invert(0)" 
                }}
              />
            </Link>

            {/* Book Button */}
            <a
              href="https://www.phorest.com/salon/jsblondsalonughaftungsbeschrae"
              target="_blank"
              rel="noopener noreferrer"
              className={`px-4 py-2 rounded-full uppercase tracking-widest text-xs font-bold ${
                (isScrolled || !isHomePage)
                  ? "bg-[#2C2C2C] text-white"
                  : "bg-white text-[#2C2C2C]"
              }`}
            >
              Buchen
            </a>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#F9F7F2] pt-24"
          >
            <nav className="flex flex-col items-center gap-8 py-12">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-2xl font-light tracking-wide transition-colors ${
                  isActive("/") ? "text-[#9A8C73]" : "text-[#2C2C2C]"
                }`}
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Home
              </Link>
              {allLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.external ? (
                    <a
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-2xl font-light tracking-wide transition-colors text-[#2C2C2C] hover:text-[#9A8C73]"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${link.path.replace('/', '')}`}
                      className={`text-2xl font-light tracking-wide transition-colors ${
                      isActive(link.path) ? "text-[#9A8C73]" : "text-[#2C2C2C]"
                    }`}
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {link.label}
                  </Link>
                  )}
                </motion.div>
              ))}
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                href="https://www.phorest.com/salon/jsblondsalonughaftungsbeschrae"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-btn"
                className="btn-primary mt-4"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Termin Buchen
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
